// deliverGroup.mjs — 密送落点组的纯逻辑（第二百零七批，SB-180 (e) 实撞修）。
//
// ── 为什么这个模块存在 ──────────────────────────────────────────────────────
// 管理面 deliverInstead 此前由后端自动取收件人**首组**落密件——多组收件人落错组
// 是实撞过的事故（distill 凭据两枚落进 MirrorMii 组、密件标签元数据对外线可见，
// 撤两层重签收场）。修法是 fail-loud：请求体带上显式 `toGroupId`，多组收件人
// **必须**在表单里选定落点组，系统不替任何人猜。
//
// 两处发放表单（Git 访问控制 / API key）都要这套判断，抽成一份纯函数——同一条
// 规则两份手写实现就是两处会各自漂的解析（第五十六批的老教训，groupLabel 同理）。
import { groupLabel } from '../api/groups.mjs';

// 收件人可作密件落点的组 id 列表。fail-closed：判不出数组就当没有组（与
// gitAccess.mjs 的 accountHasGroup 同一条口径——宁可让管理员多看一眼账号页）。
// null/undefined 的 id 滤掉：一个 String() 出来是 'null' 的选项选了也发不出去。
export function deliveryGroupIds(account) {
  if (!account || !Array.isArray(account.projectGroupIds)) return [];
  return account.projectGroupIds.filter((id) => id != null);
}

// 「要不要展示组下拉」＝收件人多于一组。单组不展示：唯一的落点没有可选的余地，
// 摆一个只有一项还必选的下拉是在向管理员要一次没有信息量的点击。
export function needsGroupChoice(account) {
  return deliveryGroupIds(account).length > 1;
}

// 随请求发出的 toGroupId。返回值取自 projectGroupIds 里的**原元素**（不经
// String/Number 改铸）：组 id 的类型是后端的话语权，前端只负责挑，不负责转。
//   · 无组      → null（页面另有 noGroup 拦截，这里兜底）
//   · 单组      → 唯一那个 id（不展示下拉也照带——显式落点让后端的校验路径
//                 对单组/多组收件人是同一条，而不是留一半继续走"取首组"的老路）
//   · 多组已选  → 选中的那个 id（选择必须真在他的组里，不在就当没选）
//   · 多组未选  → null（调用方据此禁发——多组不选就发正是实撞的那个形状）
export function deliveryToGroupId(account, selectedId) {
  const ids = deliveryGroupIds(account);
  if (ids.length === 0) return null;
  if (ids.length === 1) return ids[0];
  if (selectedId == null || selectedId === '') return null;
  return ids.find((id) => String(id) === String(selectedId)) ?? null;
}

// GET /api/admin/project-groups 的三种历史形态都收（AdminAccountsPage 的 groupsOf
// 同一条容错；抽来这里是因为本批第二、第三个页面也要读组列表——同一条容错写三遍
// 就是三处会各自漂的解析）。
export function groupListOf(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.projectGroups)) return data.projectGroups;
  if (Array.isArray(data?.groups)) return data.groups;
  return [];
}

// 组下拉的选项：[{ id, label }]。取名走 groupLabel（组名的唯一取名规则）；
// 认不出的 id **不静默丢弃**而是显示 `#<id>`（labelGroupIds 同一条纪律）——
// 账号挂在一个已归档组上是真实存在的状态，把它藏掉，密件就可能落进一个
// 管理员根本不知道存在的清单面。
export function deliveryGroupOptions(account, groups, lang) {
  const by = new Map();
  for (const g of Array.isArray(groups) ? groups : []) {
    if (g && g.id != null) by.set(String(g.id), g);
  }
  return deliveryGroupIds(account).map((id) => {
    const g = by.get(String(id));
    return { id, label: (g && groupLabel(g, lang)) || `#${id}` };
  });
}
